import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/libs/queryKeys';
import { billingService } from '@/services/billingService';
import type { CustomerInvoiceListItem, Invoice, PaymentStatus } from '@/types/payment';

/** Year/month pair for filtering invoices by period. */
interface PeriodSelection {
  year: number | undefined;
  month: number | undefined;
}

interface UseInvoicesReturn {
  invoices: Invoice[];
  isLoading: boolean;
  isError: boolean;
  period: PeriodSelection;
  periodLabel: string;
  setPeriod: (period: PeriodSelection) => void;
  refetch: () => void;
}

function mapPaymentStatus(status: string): PaymentStatus {
  if (status === 'paid') return 'Paid';
  if (status === 'pending') return 'Pending';
  if (status === 'refunded' || status === 'partially_refunded') return 'Refunded';
  return 'Failed';
}

function formatPeriodLabel(year: number, month: number): string {
  return new Date(year, month - 1, 1).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });
}

function mapCustomerInvoice(item: CustomerInvoiceListItem): Invoice {
  return {
    id: item.id,
    invoiceNumber: item.reference,
    date: new Date(item.issued_at).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }),
    description: `${item.plan_name} membership`,
    amount: item.amount_paid_minor / 100,
    currency: item.currency,
    status: mapPaymentStatus(item.payment_state),
    documentAvailable: item.document_available,
  };
}

/** Fetches customer-owned subscription invoices for the selected period. */
export function useInvoices(): UseInvoicesReturn {
  const now = new Date();
  const [period, setPeriod] = React.useState<PeriodSelection>(() => ({
    year: now.getFullYear(),
    month: now.getMonth() + 1,
  }));

  const periodLabel = React.useMemo(() => {
    if (period.year && period.month) return formatPeriodLabel(period.year, period.month);
    const currentDate = new Date();
    return formatPeriodLabel(currentDate.getFullYear(), currentDate.getMonth() + 1);
  }, [period.year, period.month]);

  const year = period.year ?? now.getFullYear();
  const month = period.month ?? now.getMonth() + 1;
  const query = useQuery({
    queryKey: queryKeys.billing.invoices(year, month),
    queryFn: () => billingService.getInvoices({ year, month }),
  });
  const invoices = (query.data?.items ?? []).map(mapCustomerInvoice);

  return {
    invoices,
    isLoading: query.isLoading,
    isError: query.isError,
    period,
    periodLabel,
    setPeriod,
    refetch: () => void query.refetch(),
  };
}
